import toast from 'react-hot-toast';

// Create a subtle audio notification for memo alerts
const playMemoNotificationSound = () => {
  try {
    // Create a subtle beep using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create a gentle tone
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Set a pleasant, subtle frequency (A4 note)
    oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
    oscillator.type = 'sine';
    
    // Very gentle volume
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    // Short, gentle beep
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    console.log('🔊 Memo notification sound played');
  } catch (error) {
    console.log('🔇 Audio notification not available:', error);
  }
};

// Create a subtle ping sound for timer expiration
export const playTimerExpirationSound = () => {
  try {
    // Create a subtle ping using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create a gentle ping tone (higher frequency for attention)
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Set a pleasant ping frequency (C5 note - 523.25 Hz)
    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
    oscillator.type = 'sine';
    
    // Gentle volume with quick fade
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    // Short, gentle ping
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    
    console.log('🔔 Timer expiration ping played');
  } catch (error) {
    console.log('🔇 Timer ping not available:', error);
  }
};

// Create an attendance success toast with memo notification
export const createAttendanceToast = (
  studentName: string,
  studentVorname: string,
  memo?: string | null,
  options: {
    duration?: number;
    position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  } = {}
) => {
  const { duration = 6000, position = 'top-right' } = options;
  
  const baseMessage = `${studentVorname} ${studentName} wurde zur Anwesenheit hinzugefügt`;
  
  // Check if student has a memo
  const hasMemo = memo && memo.trim() !== '';
  
  if (hasMemo) {
    playMemoNotificationSound();

    const memoPreview = `${memo!.substring(0, 50)}${memo!.length > 50 ? '...' : ''}`;
    toast.success(`${baseMessage}\nNotiz vorhanden: "${memoPreview}"`, {
      duration: duration + 2000,
      position,
      style: {
        maxWidth: '400px',
        whiteSpace: 'pre-line',
      },
    });

    console.log(`📝 Memo notification shown for ${studentVorname} ${studentName}:`, memo);
  } else {
    // Standard toast without memo
    toast.success(baseMessage, {
      duration,
      position,
    });
  }
};

// Utility function to fetch student memo by Unid
export const fetchStudentMemo = async (unid: string): Promise<string | null> => {
  try {
    // This would typically be an API call, but for now we'll return null
    // The memo data should be included in the attendance broadcast or response
    return null;
  } catch (error) {
    console.error('Error fetching student memo:', error);
    return null;
  }
};
